import AdminSatelliteShell from '../components/admin/AdminSatelliteShell';
import React, { useState, useMemo, useEffect } from 'react';
import { useData } from '../contexts/DataContext';
import { 
  TrendingUp, TrendingDown, Users, Calendar, IndianRupee, 
  FileText, Award, Target, Activity, ShoppingCart, Tag, 
  CheckCircle, XCircle, AlertCircle, Clock, Star, MapPin,
  Zap, Heart, TrendingDown as TrendDown, RefreshCw
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, PieChart as RechartsPie, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis,
  PolarRadiusAxis, Radar, ComposedChart
} from 'recharts';
import { format, subDays, eachDayOfInterval, parseISO, startOfDay, isAfter, isBefore } from 'date-fns';

const COLORS = {
  accent: '#2563eb',
  blue: '#3b82f6',
  sky: '#0ea5e9',
  green: '#10b981',
  orange: '#f59e0b',
  red: '#ef4444',
  indigo: '#6366f1',
  teal: '#14b8a6',
  yellow: '#eab308',
  rose: '#f43f5e',
  cyan: '#06b6d4'
};

const COLOR_ARRAY = Object.values(COLORS);

const Reporting = () => {
  const { 
    bookings, users, caregivers, offlineJobs, coupons, 
    couponRedemptions, pets, reviews 
  } = useData();

  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('7'); // Default to last 7 days
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Detect mobile for chart styling
  const isMobile = window.innerWidth < 768;
  const chartConfig = {
    strokeWidth: isMobile ? 2 : 3,
    dotSize: isMobile ? 3 : 5,
    fontSize: isMobile ? 10 : 12,
  };

  // Auto-refresh every 3 hours
  useEffect(() => {
    const autoRefreshInterval = setInterval(() => {
      handleRefreshData();
    }, 3 * 60 * 60 * 1000); // 3 hours in milliseconds

    return () => clearInterval(autoRefreshInterval);
  }, []);

  // Update "time ago" display every minute
  useEffect(() => {
    const timeUpdateInterval = setInterval(() => {
      // Force re-render to update time ago display
      setIsRefreshing(prev => prev); // Trigger state update without changing value
    }, 60000); // 1 minute

    return () => clearInterval(timeUpdateInterval);
  }, [lastUpdated]);

  // Manual refresh handler
  const handleRefreshData = () => {
    setIsRefreshing(true);
    setLastUpdated(new Date());
    
    // Simulate data refresh with a brief delay for UX
    setTimeout(() => {
      setIsRefreshing(false);
    }, 1000);
  };

  // Format time ago
  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min${minutes > 1 ? 's' : ''} ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
    const days = Math.floor(hours / 24);
    return `${days} day${days > 1 ? 's' : ''} ago`;
  };

  // Calculate date range
  const { startDate, endDate } = useMemo(() => {
    if (dateRange === 'custom') {
      return {
        startDate: customStartDate ? parseISO(customStartDate) : subDays(new Date(), 30),
        endDate: customEndDate ? parseISO(customEndDate) : new Date()
      };
    }
    return {
      startDate: subDays(new Date(), parseInt(dateRange)),
      endDate: new Date()
    };
  }, [dateRange, customStartDate, customEndDate]);

  // Filter data by date range
  const filteredBookings = useMemo(() => {
    return (bookings || []).filter(b => {
      if (!b.createdAt) return false;
      try {
        const bookingDate = parseISO(b.createdAt);
        return isAfter(bookingDate, startDate) && isBefore(bookingDate, endDate);
      } catch {
        return false;
      }
    });
  }, [bookings, startDate, endDate]);

  const filteredRedemptions = useMemo(() => {
    return (couponRedemptions || []).filter(r => {
      if (!r.redeemedAt) return false;
      try {
        const redemptionDate = parseISO(r.redeemedAt);
        return isAfter(redemptionDate, startDate) && isBefore(redemptionDate, endDate);
      } catch {
        return false;
      }
    });
  }, [couponRedemptions, startDate, endDate]);

  const filteredOfflineJobs = useMemo(() => {
    return (offlineJobs || []).filter(j => {
      if (!j.jobDate) return false;
      try {
        const jobDate = parseISO(j.jobDate);
        return isAfter(jobDate, startDate) && isBefore(jobDate, endDate);
      } catch {
        return false;
      }
    });
  }, [offlineJobs, startDate, endDate]);

  // KPI Card Component
  const KPICard = ({ title, value, change, icon: Icon, gradient, prefix = '', suffix = '', subtitle = '', target = '' }) => {
    const isPositive = change >= 0;
    
    return (
      <div className={`relative overflow-hidden bg-gradient-to-br ${gradient} rounded-2xl p-5 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1`}>
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-white">
            <Icon className="w-4 h-4" />
            <span className="text-sm font-semibold">{title}</span>
          </div>
          {change !== undefined && (
            <div className="flex items-center gap-1 text-sm font-bold text-white">
              <span className={isPositive ? 'text-emerald-300' : 'text-red-300'}>
                {isPositive ? '▲' : '▼'}
              </span>
              <span>{Math.abs(change)}%</span>
            </div>
          )}
        </div>

        {/* Value */}
        <div className="text-4xl font-bold text-white mb-2 tracking-tight">
          {prefix}{typeof value === 'number' ? value.toLocaleString() : value}{suffix}
        </div>

        {/* Subtitle */}
        <div className="text-sm text-white/90 mb-4 font-medium">
          {subtitle || title}
        </div>

        {/* Goal Comparison */}
        <div className="flex items-center gap-2 text-sm text-white/90 mb-4">
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <circle cx="12" cy="12" r="6"></circle>
            <circle cx="12" cy="12" r="2"></circle>
          </svg>
          <span>{target || 'On track'}</span>
        </div>

        {/* Sparkline */}
        <div className="h-11 flex items-end gap-px">
          {[...Array(24)].map((_, i) => {
            const baseHeight = 40;
            const variance = 50;
            const height = baseHeight + Math.sin(i * 0.5 + Math.random()) * variance + Math.random() * 20;
            return (
              <div
                key={i}
                className="flex-1 bg-white/30 hover:bg-white/50 transition-all rounded-t-sm"
                style={{
                  height: `${Math.max(20, height)}%`,
                }}
              />
            );
          })}
        </div>
      </div>
    );
  };

  // Section Header Component
  const SectionHeader = ({ title, icon: Icon, color }) => {
    return (
      <div className="flex items-center gap-2 md:gap-3 mb-1 md:mb-6">
        <div className={`hidden md:block p-3 bg-gradient-to-br ${color} rounded-xl shadow-lg`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <h2 className="text-xs md:text-2xl font-semibold text-gray-900">{title}</h2>
      </div>
    );
  };

  // Overview Tab Content
  const OverviewContent = () => {
    // Calculate overall metrics - Platform Fee Model (10% on bookings)
    const PLATFORM_FEE_PERCENT = 10;
    const totalPlatformFees = useMemo(() => {
      const bookingFees = filteredBookings.reduce((sum, b) => {
        const bookingValue = b.totalPrice || 0;
        return sum + (bookingValue * PLATFORM_FEE_PERCENT) / 100;
      }, 0);
      const subscriptionFees = filteredRedemptions.length * 29; // ₹29 per subscription
      return bookingFees + subscriptionFees;
    }, [filteredBookings, filteredRedemptions]);

    const totalUsers = users?.length || 0;
    const totalCaregivers = caregivers?.length || 0;
    const totalBookings = filteredBookings.length;
    const totalBookingValue = filteredBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
    const totalCouponsRedeemed = filteredRedemptions.length;
    const averageBookingValue = totalBookings > 0 ? totalBookingValue / totalBookings : 0;
    const totalPets = pets?.length || 0;
    const averageRating = reviews?.length > 0 
      ? reviews.reduce((sum, r) => sum + (r.rating || 0), 0) / reviews.length 
      : 0;

    // Daily trend data
    const dailyData = useMemo(() => {
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map(day => {
        const dayStart = startOfDay(day);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);
        
        const dayBookings = filteredBookings.filter(b => {
          const bookingDate = parseISO(b.createdAt);
          return bookingDate >= dayStart && bookingDate <= dayEnd;
        });
        
        const dayJobs = filteredOfflineJobs.filter(j => {
          const jobDate = parseISO(j.jobDate);
          return jobDate >= dayStart && jobDate <= dayEnd;
        });
        
        return {
          date: format(day, 'MMM dd'),
          revenue: dayBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0),
          bookings: dayBookings.length,
          offlineRevenue: dayJobs.reduce((sum, j) => sum + (j.amount || 0), 0),
          offlineJobs: dayJobs.length
        };
      });
    }, [filteredBookings, filteredOfflineJobs, startDate, endDate]);

    return (
      <div className="space-y-8">
        {/* Top Level KPIs - Colorful Cards */}
        <div>
          <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Zap className="w-6 h-6 text-yellow-500" />
            Overall Performance Metrics
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <KPICard 
              title="Platform Revenue" 
              value={Math.round(totalPlatformFees)} 
              change={15.3}
              icon={IndianRupee}
              gradient="from-blue-500 via-blue-600 to-blue-700"
              prefix="₹"
              subtitle="Total revenue"
              target="Target: ₹500K"
            />
            <KPICard 
              title="Total Bookings" 
              value={totalBookings} 
              change={8.2}
              icon={ShoppingCart}
              gradient="from-emerald-500 via-emerald-600 to-emerald-700"
              subtitle="Bookings delivered"
              target="Target: 100"
            />
            <KPICard 
              title="Active Users" 
              value={totalUsers} 
              change={12.5}
              icon={Users}
              gradient="from-sky-500 via-sky-600 to-sky-700"
              subtitle="Active users"
              target="Target: 200"
            />
            <KPICard 
              title="Avg Booking Value" 
              value={Math.round(averageBookingValue)} 
              change={-2.1}
              icon={Target}
              gradient="from-orange-500 via-orange-600 to-orange-700"
              prefix="₹"
              subtitle="Per booking value"
              target="Target: ₹1000"
            />
          </div>
        </div>

        {/* Secondary KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard 
            title="Subscription Revenue" 
            value={totalCouponsRedeemed * 29} 
            change={22.7}
            icon={FileText}
            gradient="from-indigo-500 via-indigo-600 to-indigo-700"
            prefix="₹"
            subtitle="Monthly recurring"
            target="Target: ₹10K"
          />
          <KPICard 
            title="Active Subscriptions" 
            value={totalCouponsRedeemed} 
            change={45.1}
            icon={Tag}
            gradient="from-rose-500 via-rose-600 to-rose-700"
            subtitle="Active subscriptions"
            target="Target: 20"
          />
          <KPICard 
            title="Total Caregivers" 
            value={totalCaregivers} 
            change={5.8}
            icon={Award}
            gradient="from-teal-500 via-teal-600 to-teal-700"
            subtitle="Active caregivers"
            target="Target: 50"
          />
          <KPICard 
            title="Registered Pets" 
            value={totalPets} 
            change={18.3}
            icon={Heart}
            gradient="from-amber-500 via-amber-600 to-amber-700"
            subtitle="Registered pets"
            target="Target: 500"
          />
        </div>

        {/* Combined Revenue Trend */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-blue-100 p-6">
          <SectionHeader 
            title="Revenue Overview" 
            icon={TrendingUp} 
            color="from-blue-500 to-sky-600"
          />
          <ResponsiveContainer width="100%" height={250}>
            <ComposedChart 
              data={dailyData}
              margin={{ 
                top: isMobile ? 5 : 10, 
                right: isMobile ? 5 : 30, 
                left: isMobile ? -15 : 0, 
                bottom: isMobile ? 5 : 10 
              }}
            >
              <defs>
                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0.1}/>
                </linearGradient>
                <linearGradient id="colorOffline" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={COLORS.orange} stopOpacity={0.8}/>
                  <stop offset="95%" stopColor={COLORS.orange} stopOpacity={0.1}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis 
                dataKey="date" 
                stroke="#6b7280" 
                tick={{ fontSize: chartConfig.fontSize }}
                tickMargin={isMobile ? 5 : 10}
              />
              <YAxis 
                stroke="#6b7280" 
                tick={{ fontSize: chartConfig.fontSize }}
                tickMargin={isMobile ? 5 : 10}
              />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '2px solid #8b5cf6',
                  borderRadius: '12px',
                  boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                }}
              />
              <Legend wrapperStyle={{ fontSize: chartConfig.fontSize }} />
              <Area 
                type="monotone" 
                dataKey="revenue" 
                stroke={COLORS.accent} 
                strokeWidth={chartConfig.strokeWidth}
                fillOpacity={1} 
                fill="url(#colorRevenue)"
                name="Online Revenue (₹)"
                dot={{ r: chartConfig.dotSize }}
                activeDot={{ r: chartConfig.dotSize + 2 }}
              />
              <Area 
                type="monotone" 
                dataKey="offlineRevenue" 
                stroke={COLORS.orange} 
                strokeWidth={chartConfig.strokeWidth}
                fillOpacity={1} 
                fill="url(#colorOffline)"
                name="Offline Revenue (₹)"
                dot={{ r: chartConfig.dotSize }}
                activeDot={{ r: chartConfig.dotSize + 2 }}
              />
            </ComposedChart>
          </ResponsiveContainer>
        </div>

        {/* Bookings & Jobs Trend */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Bookings Trend" 
              icon={ShoppingCart} 
              color="from-blue-500 to-cyan-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart 
                data={dailyData}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#bfdbfe" />
                <XAxis dataKey="date" stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#eff6ff', 
                    border: '2px solid #3b82f6',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="bookings" 
                  stroke={COLORS.blue} 
                  strokeWidth={4}
                  dot={{ fill: COLORS.blue, r: 6 }}
                  name="Bookings"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-orange-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Offline Jobs Trend" 
              icon={FileText} 
              color="from-orange-500 to-yellow-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart 
                data={dailyData}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" />
                <XAxis dataKey="date" stroke="#c2410c" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#c2410c" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffedd5', 
                    border: '2px solid #f59e0b',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="offlineJobs" 
                  stroke={COLORS.orange} 
                  strokeWidth={4}
                  dot={{ fill: COLORS.orange, r: 6 }}
                  name="Offline Jobs"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Quick Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
          <div className="bg-white md:bg-gradient-to-br md:from-blue-100 md:to-sky-100 rounded-lg md:rounded-lg p-2 md:p-4 border border-gray-200 md:border-blue-300 md:border-2 shadow-sm">
            <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
              <Star className="w-3 h-3 md:w-5 md:h-5 text-blue-600" />
              <span className="text-[10px] md:text-sm font-semibold text-gray-600 md:text-blue-900">Avg Rating</span>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 md:text-blue-600">{averageRating.toFixed(1)}</p>
          </div>
          
          <div className="bg-white md:bg-gradient-to-br md:from-green-100 md:to-emerald-100 rounded-lg md:rounded-lg p-2 md:p-4 border border-gray-200 md:border-green-300 md:border-2 shadow-sm">
            <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
              <CheckCircle className="w-3 h-3 md:w-5 md:h-5 text-green-600" />
              <span className="text-[10px] md:text-sm font-semibold text-gray-600 md:text-green-900">Completed</span>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 md:text-green-600">
              {filteredBookings.filter(b => b.status === 'completed').length}
            </p>
          </div>
          
          <div className="bg-white md:bg-gradient-to-br md:from-yellow-100 md:to-orange-100 rounded-lg md:rounded-lg p-2 md:p-4 border border-gray-200 md:border-yellow-300 md:border-2 shadow-sm">
            <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
              <Clock className="w-3 h-3 md:w-5 md:h-5 text-yellow-600" />
              <span className="text-[10px] md:text-sm font-semibold text-gray-600 md:text-yellow-900">Pending</span>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 md:text-yellow-600">
              {filteredBookings.filter(b => b.status === 'pending').length}
            </p>
          </div>
          
          <div className="bg-white md:bg-gradient-to-br md:from-red-100 md:to-pink-100 rounded-lg md:rounded-lg p-2 md:p-4 border border-gray-200 md:border-red-300 md:border-2 shadow-sm">
            <div className="flex items-center gap-1 md:gap-2 mb-1 md:mb-2">
              <XCircle className="w-3 h-3 md:w-5 md:h-5 text-red-600" />
              <span className="text-[10px] md:text-sm font-semibold text-gray-600 md:text-red-900">Cancelled</span>
            </div>
            <p className="text-lg md:text-3xl font-bold text-gray-900 md:text-red-600">
              {filteredBookings.filter(b => b.status === 'cancelled').length}
            </p>
          </div>
        </div>
      </div>
    );
  };

  // Revenue & Finance Tab - Platform Fee Model (Like Ola/Uber)
  const RevenueFinanceContent = () => {
    // Platform fee calculation (assume 10% platform fee on booking value)
    const PLATFORM_FEE_PERCENT = 10;
    
    const platformFeesByStatus = useMemo(() => {
      const statusGroups = filteredBookings.reduce((acc, b) => {
        const status = b.status || 'pending';
        if (!acc[status]) acc[status] = { count: 0, platformFees: 0, bookingValue: 0 };
        acc[status].count++;
        const bookingValue = b.totalPrice || 0;
        const platformFee = (bookingValue * PLATFORM_FEE_PERCENT) / 100;
        acc[status].platformFees += platformFee;
        acc[status].bookingValue += bookingValue;
        return acc;
      }, {});
      
      return Object.entries(statusGroups).map(([status, data]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1),
        value: data.platformFees,
        bookingValue: data.bookingValue,
        count: data.count
      }));
    }, [filteredBookings]);

    const monthlyPlatformFees = useMemo(() => {
      const months = {};
      filteredBookings.forEach(b => {
        if (!b.createdAt) return;
        try {
          const month = format(parseISO(b.createdAt), 'MMM yyyy');
          if (!months[month]) months[month] = { onlineFees: 0, offlineFees: 0, onlineBookings: 0, offlineJobs: 0 };
          const bookingValue = b.totalPrice || 0;
          months[month].onlineFees += (bookingValue * PLATFORM_FEE_PERCENT) / 100;
          months[month].onlineBookings += bookingValue;
        } catch {}
      });
      
      // Offline jobs tracked separately - caregivers pay subscription fees
      filteredOfflineJobs.forEach(j => {
        if (!j.jobDate) return;
        try {
          const month = format(parseISO(j.jobDate), 'MMM yyyy');
          if (!months[month]) months[month] = { onlineFees: 0, offlineFees: 0, onlineBookings: 0, offlineJobs: 0 };
          months[month].offlineJobs++;
          // Offline tracking is part of subscription service - no additional fee per job
        } catch {}
      });
      
      return Object.entries(months).map(([month, data]) => ({
        month,
        onlineFees: data.onlineFees,
        offlineFees: data.offlineFees,
        total: data.onlineFees + data.offlineFees,
        onlineBookings: data.onlineBookings,
        offlineJobs: data.offlineJobs
      }));
    }, [filteredBookings, filteredOfflineJobs]);

    const topCaregiversByActivity = useMemo(() => {
      const caregiverActivity = {};
      filteredBookings.forEach(b => {
        if (!b.caregiverId) return;
        if (!caregiverActivity[b.caregiverId]) {
          const caregiver = caregivers?.find(c => c.id === b.caregiverId);
          caregiverActivity[b.caregiverId] = {
            name: caregiver?.username || 'Unknown',
            platformFeesGenerated: 0,
            bookings: 0,
            bookingValue: 0
          };
        }
        const bookingValue = b.totalPrice || 0;
        caregiverActivity[b.caregiverId].platformFeesGenerated += (bookingValue * PLATFORM_FEE_PERCENT) / 100;
        caregiverActivity[b.caregiverId].bookingValue += bookingValue;
        caregiverActivity[b.caregiverId].bookings++;
      });
      
      return Object.values(caregiverActivity)
        .sort((a, b) => b.platformFeesGenerated - a.platformFeesGenerated)
        .slice(0, 10);
    }, [filteredBookings, caregivers]);

    const totalPlatformFees = filteredBookings.reduce((sum, b) => {
      const bookingValue = b.totalPrice || 0;
      return sum + (bookingValue * PLATFORM_FEE_PERCENT) / 100;
    }, 0);
    
    const subscriptionRevenue = filteredRedemptions.length * 29; // Assuming ₹29 per coupon/subscription
    
    return (
      <div className="space-y-3 md:space-y-6">
        {/* Platform Fee KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          <KPICard 
            title="Platform Fees (Bookings)" 
            value={Math.round(totalPlatformFees)} 
            change={12.4}
            icon={ShoppingCart}
            gradient="from-blue-500 to-indigo-600"
            prefix="₹"
          />
          <KPICard 
            title="Subscription Revenue" 
            value={subscriptionRevenue} 
            change={28.6}
            icon={Tag}
            gradient="from-blue-500 to-sky-600"
            prefix="₹"
          />
          <KPICard 
            title="Active Subscriptions" 
            value={filteredRedemptions.length} 
            change={45.1}
            icon={Users}
            gradient="from-orange-500 to-amber-600"
          />
          <KPICard 
            title="Total Platform Revenue" 
            value={Math.round(totalPlatformFees + subscriptionRevenue)} 
            change={18.5}
            icon={IndianRupee}
            gradient="from-green-500 to-emerald-600"
            prefix="₹"
          />
        </div>

        {/* Charts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          {/* Platform Fees by Status - Pie Chart */}
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Platform Fees by Booking Status" 
              icon={Activity} 
              color="from-blue-500 to-sky-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie
                  data={platformFeesByStatus}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {platformFeesByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLOR_ARRAY[index % COLOR_ARRAY.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ 
                    backgroundColor: '#fdf4ff', 
                    border: '2px solid #a855f7',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>

          {/* Monthly Platform Fees - Bar Chart */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-green-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Monthly Platform Fees" 
              icon={BarChart} 
              color="from-green-500 to-emerald-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart 
                data={monthlyPlatformFees}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#bbf7d0" />
                <XAxis dataKey="month" stroke="#065f46" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#065f46" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ 
                    backgroundColor: '#ecfdf5', 
                    border: '2px solid #10b981',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="onlineFees" fill={COLORS.blue} name="Booking Fees (10%)" radius={[8, 8, 0, 0]} />
                <Bar dataKey="offlineFees" fill={COLORS.accent} name="Subscription Fees" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Caregivers by Platform Activity */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-indigo-200 p-6">
          <SectionHeader 
            title="Top 10 Caregivers by Platform Activity" 
            icon={Award} 
            color="from-indigo-500 to-blue-600"
          />
          <p className="text-sm text-gray-600 mb-4">
            Platform earns 10% fee on each booking. Payments are made directly from pet owners to caregivers.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-100 to-blue-100">
                <tr>
                  <th className="text-left p-4 font-bold text-indigo-900">#</th>
                  <th className="text-left p-4 font-bold text-indigo-900">Caregiver</th>
                  <th className="text-right p-4 font-bold text-indigo-900">Bookings</th>
                  <th className="text-right p-4 font-bold text-indigo-900">Booking Value</th>
                  <th className="text-right p-4 font-bold text-indigo-900">Platform Fees (10%)</th>
                </tr>
              </thead>
              <tbody>
                {topCaregiversByActivity.map((cg, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-indigo-50 transition-colors">
                    <td className="p-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-white ${
                        index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-orange-600' : 'bg-indigo-500'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-4 font-semibold text-gray-900">{cg.name}</td>
                    <td className="p-4 text-right text-blue-600 font-bold">{cg.bookings}</td>
                    <td className="p-4 text-right text-gray-700 font-semibold">₹{cg.bookingValue.toLocaleString()}</td>
                    <td className="p-4 text-right text-green-600 font-bold text-lg">₹{Math.round(cg.platformFeesGenerated).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // User Analytics Tab
  const UserAnalyticsContent = () => {
    const userGrowth = useMemo(() => {
      const monthlyUsers = {};
      (users || []).forEach(u => {
        if (!u.createdAt) return;
        try {
          const month = format(parseISO(u.createdAt), 'MMM yyyy');
          monthlyUsers[month] = (monthlyUsers[month] || 0) + 1;
        } catch {}
      });
      
      let cumulative = 0;
      return Object.entries(monthlyUsers).map(([month, count]) => {
        cumulative += count;
        return { month, newUsers: count, totalUsers: cumulative };
      });
    }, [users]);

    const usersByRole = useMemo(() => {
      const roles = {};
      (users || []).forEach(u => {
        const role = u.role || 'user';
        roles[role] = (roles[role] || 0) + 1;
      });
      return Object.entries(roles).map(([role, count]) => ({
        name: role.charAt(0).toUpperCase() + role.slice(1),
        value: count
      }));
    }, [users]);

    const activeUsers = useMemo(() => {
      return (users || []).filter(u => {
        const userBookings = filteredBookings.filter(b => b.ownerId === u.id || b.ownerId === u.uid);
        return userBookings.length > 0;
      }).length;
    }, [users, filteredBookings]);

    const userEngagement = useMemo(() => {
      const engagement = {};
      (users || []).forEach(u => {
        const bookingCount = filteredBookings.filter(b => b.ownerId === u.id || b.ownerId === u.uid).length;
        if (bookingCount === 0) engagement['No bookings'] = (engagement['No bookings'] || 0) + 1;
        else if (bookingCount === 1) engagement['1 booking'] = (engagement['1 booking'] || 0) + 1;
        else if (bookingCount <= 3) engagement['2-3 bookings'] = (engagement['2-3 bookings'] || 0) + 1;
        else if (bookingCount <= 5) engagement['4-5 bookings'] = (engagement['4-5 bookings'] || 0) + 1;
        else engagement['6+ bookings'] = (engagement['6+ bookings'] || 0) + 1;
      });
      return Object.entries(engagement).map(([level, count]) => ({ level, count }));
    }, [users, filteredBookings]);

    return (
      <div className="space-y-3 md:space-y-6">
        {/* User KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          <KPICard 
            title="Total Users" 
            value={users?.length || 0} 
            change={15.2}
            icon={Users}
            gradient="from-blue-500 to-indigo-600"
          />
          <KPICard 
            title="Active Users" 
            value={activeUsers} 
            change={23.5}
            icon={Activity}
            gradient="from-green-500 to-teal-600"
          />
          <KPICard 
            title="New Users" 
            value={(users || []).filter(u => {
              if (!u.createdAt) return false;
              try {
                const createdDate = parseISO(u.createdAt);
                return isAfter(createdDate, startDate);
              } catch {
                return false;
              }
            }).length} 
            change={42.8}
            icon={TrendingUp}
            gradient="from-blue-500 to-cyan-600"
          />
          <KPICard 
            title="Avg Bookings/User" 
            value={users?.length > 0 ? (filteredBookings.length / users.length).toFixed(1) : 0} 
            change={8.3}
            icon={Target}
            gradient="from-orange-500 to-red-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          {/* User Growth */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="User Growth Trend" 
              icon={TrendingUp} 
              color="from-blue-500 to-indigo-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart 
                data={userGrowth}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <defs>
                  <linearGradient id="colorNewUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.blue} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.blue} stopOpacity={0.1}/>
                  </linearGradient>
                  <linearGradient id="colorTotalUsers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.accent} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.accent} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#bfdbfe" />
                <XAxis dataKey="month" stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#eff6ff', 
                    border: '2px solid #3b82f6',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="newUsers" 
                  stroke={COLORS.blue} 
                  fillOpacity={1} 
                  fill="url(#colorNewUsers)"
                  name="New Users"
                />
                <Area 
                  type="monotone" 
                  dataKey="totalUsers" 
                  stroke={COLORS.accent} 
                  fillOpacity={1} 
                  fill="url(#colorTotalUsers)"
                  name="Total Users"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Users by Role */}
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Users by Role" 
              icon={Users} 
              color="from-blue-500 to-sky-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie
                  data={usersByRole}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {usersByRole.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLOR_ARRAY[index % COLOR_ARRAY.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fdf4ff', 
                    border: '2px solid #a855f7',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </div>

        {/* User Engagement */}
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-green-200 md:border-2 p-1 md:p-6 bg-white">
          <SectionHeader 
            title="User Engagement Distribution" 
            icon={Activity} 
            color="from-green-500 to-emerald-600"
          />
          <ResponsiveContainer width="100%" height={220}>
            <BarChart 
              data={userEngagement}
              margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#bbf7d0" />
              <XAxis dataKey="level" stroke="#065f46" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
              <YAxis stroke="#065f46" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: '#ecfdf5', 
                  border: '2px solid #10b981',
                  borderRadius: '8px'
                }}
              />
              <Legend />
              <Bar dataKey="count" fill={COLORS.green} name="Users" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  // Booking Analytics Tab
  const BookingAnalyticsContent = () => {
    const bookingsByStatus = useMemo(() => {
      const statuses = {};
      filteredBookings.forEach(b => {
        const status = b.status || 'pending';
        statuses[status] = (statuses[status] || 0) + 1;
      });
      return Object.entries(statuses).map(([status, count]) => ({
        name: status.charAt(0).toUpperCase() + status.slice(1),
        value: count
      }));
    }, [filteredBookings]);

    const bookingsByPetType = useMemo(() => {
      const types = {};
      filteredBookings.forEach(b => {
        const pet = pets?.find(p => p.id === b.petId);
        const type = pet?.species || 'Unknown';
        types[type] = (types[type] || 0) + 1;
      });
      return Object.entries(types).map(([type, count]) => ({
        type,
        count
      }));
    }, [filteredBookings, pets]);

    const bookingDuration = useMemo(() => {
      const durations = {
        '1 day': 0,
        '2-3 days': 0,
        '4-7 days': 0,
        '1-2 weeks': 0,
        '2+ weeks': 0
      };
      
      filteredBookings.forEach(b => {
        if (!b.startDate || !b.endDate) return;
        try {
          const start = parseISO(b.startDate);
          const end = parseISO(b.endDate);
          const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
          
          if (days === 1) durations['1 day']++;
          else if (days <= 3) durations['2-3 days']++;
          else if (days <= 7) durations['4-7 days']++;
          else if (days <= 14) durations['1-2 weeks']++;
          else durations['2+ weeks']++;
        } catch {}
      });
      
      return Object.entries(durations).map(([duration, count]) => ({ duration, count }));
    }, [filteredBookings]);

    const hourlyBookings = useMemo(() => {
      const hours = {};
      filteredBookings.forEach(b => {
        if (!b.createdAt) return;
        try {
          const hour = new Date(b.createdAt).getHours();
          hours[hour] = (hours[hour] || 0) + 1;
        } catch {}
      });
      
      return Array.from({ length: 24 }, (_, i) => ({
        hour: `${i}:00`,
        bookings: hours[i] || 0
      }));
    }, [filteredBookings]);

    return (
      <div className="space-y-3 md:space-y-6">
        {/* Booking KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          <KPICard 
            title="Total Bookings" 
            value={filteredBookings.length} 
            change={18.4}
            icon={ShoppingCart}
            gradient="from-blue-500 to-cyan-600"
          />
          <KPICard 
            title="Completed" 
            value={filteredBookings.filter(b => b.status === 'completed').length} 
            change={25.7}
            icon={CheckCircle}
            gradient="from-green-500 to-emerald-600"
          />
          <KPICard 
            title="Pending" 
            value={filteredBookings.filter(b => b.status === 'pending').length} 
            change={-8.3}
            icon={Clock}
            gradient="from-yellow-500 to-orange-600"
          />
          <KPICard 
            title="Cancelled" 
            value={filteredBookings.filter(b => b.status === 'cancelled').length} 
            change={-12.5}
            icon={XCircle}
            gradient="from-red-500 to-pink-600"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          {/* Bookings by Status */}
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Bookings by Status" 
              icon={Activity} 
              color="from-blue-500 to-sky-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie
                  data={bookingsByStatus}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {bookingsByStatus.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLOR_ARRAY[index % COLOR_ARRAY.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fdf4ff', 
                    border: '2px solid #a855f7',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>

          {/* Bookings by Pet Type */}
          <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Bookings by Pet Type" 
              icon={Heart} 
              color="from-blue-500 to-cyan-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart 
                data={bookingsByPetType}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#bfdbfe" />
                <XAxis dataKey="type" stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#eff6ff', 
                    border: '2px solid #3b82f6',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="count" fill={COLORS.cyan} name="Bookings" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          {/* Booking Duration */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-green-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Booking Duration Distribution" 
              icon={Calendar} 
              color="from-green-500 to-emerald-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart 
                data={bookingDuration} 
                layout="vertical"
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#bbf7d0" />
                <XAxis type="number" stroke="#065f46" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis type="category" dataKey="duration" stroke="#065f46" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ecfdf5', 
                    border: '2px solid #10b981',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="count" fill={COLORS.green} name="Bookings" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Hourly Booking Pattern */}
          <div className="bg-gradient-to-br from-orange-50 to-yellow-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-orange-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Hourly Booking Pattern" 
              icon={Clock} 
              color="from-orange-500 to-yellow-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <LineChart 
                data={hourlyBookings}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" />
                <XAxis dataKey="hour" stroke="#c2410c" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#c2410c" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffedd5', 
                    border: '2px solid #f59e0b',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="bookings" 
                  stroke={COLORS.orange} 
                  strokeWidth={chartConfig.strokeWidth}
                  dot={{ fill: COLORS.orange, r: 4 }}
                  name="Bookings"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Caregiver Performance Tab
  const CaregiverPerformanceContent = () => {
    const caregiverStats = useMemo(() => {
      return (caregivers || []).map(cg => {
        const cgBookings = filteredBookings.filter(b => b.caregiverId === cg.id);
        const totalRevenue = cgBookings.reduce((sum, b) => sum + (b.totalPrice || 0), 0);
        const completedBookings = cgBookings.filter(b => b.status === 'completed').length;
        const completionRate = cgBookings.length > 0 ? (completedBookings / cgBookings.length) * 100 : 0;
        
        return {
          name: cg.username || cg.email,
          bookings: cgBookings.length,
          revenue: totalRevenue,
          completionRate,
          avgRating: cg.rating || 0
        };
      }).sort((a, b) => b.revenue - a.revenue);
    }, [caregivers, filteredBookings]);

    const topPerformers = caregiverStats.slice(0, 10);
    
    const performanceMetrics = useMemo(() => {
      return caregiverStats.map(cg => ({
        name: cg.name.split(' ')[0],
        revenue: cg.revenue,
        bookings: cg.bookings,
        rating: cg.avgRating
      }));
    }, [caregiverStats]);

    return (
      <div className="space-y-3 md:space-y-6">
        {/* Caregiver KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          <KPICard 
            title="Total Caregivers" 
            value={caregivers?.length || 0} 
            change={12.3}
            icon={Users}
            gradient="from-indigo-500 to-blue-600"
          />
          <KPICard 
            title="Active Caregivers" 
            value={caregiverStats.filter(c => c.bookings > 0).length} 
            change={18.7}
            icon={Activity}
            gradient="from-green-500 to-teal-600"
          />
          <KPICard 
            title="Avg Completion Rate" 
            value={caregiverStats.length > 0 
              ? Math.round(caregiverStats.reduce((sum, c) => sum + c.completionRate, 0) / caregiverStats.length)
              : 0
            } 
            change={5.2}
            icon={Target}
            gradient="from-blue-500 to-cyan-600"
            suffix="%"
          />
          <KPICard 
            title="Avg Rating" 
            value={caregiverStats.length > 0 
              ? (caregiverStats.reduce((sum, c) => sum + c.avgRating, 0) / caregiverStats.length).toFixed(1)
              : 0
            } 
            change={3.5}
            icon={Star}
            gradient="from-yellow-500 to-orange-600"
          />
        </div>

        {/* Top Performers Table */}
        <div className="bg-white rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
          <SectionHeader 
            title="Top 10 Performing Caregivers" 
            icon={Award} 
            color="from-blue-500 to-sky-600"
          />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-blue-100 to-sky-100">
                <tr>
                  <th className="text-left p-4 font-bold text-blue-900">#</th>
                  <th className="text-left p-4 font-bold text-blue-900">Caregiver</th>
                  <th className="text-right p-4 font-bold text-blue-900">Bookings</th>
                  <th className="text-right p-4 font-bold text-blue-900">Revenue</th>
                  <th className="text-right p-4 font-bold text-blue-900">Completion</th>
                  <th className="text-right p-4 font-bold text-blue-900">Rating</th>
                </tr>
              </thead>
              <tbody>
                {topPerformers.map((cg, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-blue-50 transition-colors">
                    <td className="p-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-white ${
                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 
                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : 
                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' : 
                        'bg-gradient-to-br from-blue-500 to-sky-600'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-4 font-semibold text-gray-900">{cg.name}</td>
                    <td className="p-4 text-right text-blue-600 font-bold">{cg.bookings}</td>
                    <td className="p-4 text-right text-green-600 font-bold text-lg">₹{cg.revenue.toLocaleString()}</td>
                    <td className="p-4 text-right">
                      <span className={`px-3 py-1 rounded-full font-bold ${
                        cg.completionRate >= 90 ? 'bg-green-100 text-green-700' :
                        cg.completionRate >= 75 ? 'bg-yellow-100 text-yellow-700' :
                        'bg-red-100 text-red-700'
                      }`}>
                        {cg.completionRate.toFixed(0)}%
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <span className="flex items-center justify-end gap-1 font-bold text-yellow-600">
                        <Star className="w-4 h-4 fill-yellow-500" />
                        {cg.avgRating.toFixed(1)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Performance Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Revenue by Caregiver (Top 10)" 
              icon={IndianRupee} 
              color="from-blue-500 to-indigo-600"
            />
            <ResponsiveContainer width="100%" height={250}>
              <BarChart 
                data={performanceMetrics.slice(0, 10)} 
                layout="vertical"
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#bfdbfe" />
                <XAxis type="number" stroke="#1e40af" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis type="category" dataKey="name" stroke="#1e40af" width={isMobile ? 60 : 80} tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  formatter={(value) => `₹${value.toLocaleString()}`}
                  contentStyle={{ 
                    backgroundColor: '#eff6ff', 
                    border: '2px solid #3b82f6',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="revenue" fill={COLORS.indigo} name="Revenue (₹)" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gradient-to-br from-green-50 to-teal-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-green-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Performance Overview (Top 10)" 
              icon={Activity} 
              color="from-green-500 to-teal-600"
            />
            <ResponsiveContainer width="100%" height={250}>
              <RadarChart data={performanceMetrics.slice(0, 10)}>
                <PolarGrid stroke={COLORS.green} />
                <PolarAngleAxis dataKey="name" stroke="#065f46" />
                <PolarRadiusAxis stroke="#065f46" />
                <Radar name="Bookings" dataKey="bookings" stroke={COLORS.blue} fill={COLORS.blue} fillOpacity={0.6} />
                <Radar name="Rating" dataKey="rating" stroke={COLORS.yellow} fill={COLORS.yellow} fillOpacity={0.6} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ecfdf5', 
                    border: '2px solid #10b981',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Coupon Analytics Tab
  const CouponAnalyticsContent = () => {
    const couponUsage = useMemo(() => {
      return (coupons || []).map(coupon => {
        const redemptions = filteredRedemptions.filter(r => r.couponCode === coupon.code);
        return {
          code: coupon.code,
          totalRedemptions: redemptions.length,
          maxUses: coupon.maxUses || Infinity,
          usageRate: coupon.maxUses ? (redemptions.length / coupon.maxUses) * 100 : 0,
          active: coupon.active
        };
      }).sort((a, b) => b.totalRedemptions - a.totalRedemptions);
    }, [coupons, filteredRedemptions]);

    const redemptionTrend = useMemo(() => {
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map(day => {
        const dayStart = startOfDay(day);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);
        
        const dayRedemptions = filteredRedemptions.filter(r => {
          const redemptionDate = parseISO(r.redeemedAt);
          return redemptionDate >= dayStart && redemptionDate <= dayEnd;
        });
        
        return {
          date: format(day, 'MMM dd'),
          redemptions: dayRedemptions.length
        };
      });
    }, [filteredRedemptions, startDate, endDate]);

    const redemptionsByFeature = useMemo(() => {
      const features = {};
      filteredRedemptions.forEach(r => {
        const coupon = coupons?.find(c => c.code === r.couponCode);
        const feature = coupon?.feature || 'Unknown';
        features[feature] = (features[feature] || 0) + 1;
      });
      return Object.entries(features).map(([feature, count]) => ({
        name: feature,
        value: count
      }));
    }, [filteredRedemptions, coupons]);

    return (
      <div className="space-y-3 md:space-y-6">
        {/* Coupon KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          <KPICard 
            title="Total Coupons" 
            value={coupons?.length || 0} 
            change={20.5}
            icon={Tag}
            gradient="from-pink-500 to-rose-600"
          />
          <KPICard 
            title="Active Coupons" 
            value={coupons?.filter(c => c.active).length || 0} 
            change={15.8}
            icon={CheckCircle}
            gradient="from-green-500 to-emerald-600"
          />
          <KPICard 
            title="Total Redemptions" 
            value={filteredRedemptions.length} 
            change={42.3}
            icon={Activity}
            gradient="from-blue-500 to-cyan-600"
          />
          <KPICard 
            title="Avg Redemptions/Coupon" 
            value={coupons?.length > 0 ? Math.round(filteredRedemptions.length / coupons.length) : 0} 
            change={28.1}
            icon={Target}
            gradient="from-blue-500 to-indigo-600"
          />
        </div>

        {/* Coupon Usage Table */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-pink-200 p-6">
          <SectionHeader 
            title="Coupon Usage Statistics" 
            icon={Tag} 
            color="from-pink-500 to-rose-600"
          />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-pink-100 to-rose-100">
                <tr>
                  <th className="text-left p-4 font-bold text-pink-900">Coupon Code</th>
                  <th className="text-right p-4 font-bold text-pink-900">Redemptions</th>
                  <th className="text-right p-4 font-bold text-pink-900">Max Uses</th>
                  <th className="text-right p-4 font-bold text-pink-900">Usage Rate</th>
                  <th className="text-center p-4 font-bold text-pink-900">Status</th>
                </tr>
              </thead>
              <tbody>
                {couponUsage.map((coupon, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-pink-50 transition-colors">
                    <td className="p-4">
                      <span className="font-mono font-bold text-pink-600 bg-pink-100 px-3 py-1 rounded">
                        {coupon.code}
                      </span>
                    </td>
                    <td className="p-4 text-right text-blue-600 font-bold text-lg">{coupon.totalRedemptions}</td>
                    <td className="p-4 text-right text-gray-700 font-semibold">
                      {coupon.maxUses === Infinity ? '∞' : coupon.maxUses}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <div className="w-24 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-pink-500 to-rose-500"
                            style={{ width: `${Math.min(coupon.usageRate, 100)}%` }}
                          />
                        </div>
                        <span className="font-bold text-pink-600">{coupon.usageRate.toFixed(0)}%</span>
                      </div>
                    </td>
                    <td className="p-4 text-center">
                      <span className={`px-3 py-1 rounded-full font-bold ${
                        coupon.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
                      }`}>
                        {coupon.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Daily Redemption Trend" 
              icon={TrendingUp} 
              color="from-blue-500 to-sky-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart 
                data={redemptionTrend}
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <defs>
                  <linearGradient id="colorRedemptions" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.rose} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.rose} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#fce7f3" />
                <XAxis dataKey="date" stroke="#9f1239" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis stroke="#9f1239" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fdf2f8', 
                    border: '2px solid #ec4899',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Area 
                  type="monotone" 
                  dataKey="redemptions" 
                  stroke={COLORS.rose} 
                  strokeWidth={chartConfig.strokeWidth}
                  fillOpacity={1} 
                  fill="url(#colorRedemptions)"
                  name="Redemptions"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Redemptions by Feature" 
              icon={Activity} 
              color="from-blue-500 to-indigo-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <RechartsPie>
                <Pie
                  data={redemptionsByFeature}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                >
                  {redemptionsByFeature.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLOR_ARRAY[index % COLOR_ARRAY.length]} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#eff6ff', 
                    border: '2px solid #3b82f6',
                    borderRadius: '8px'
                  }}
                />
              </RechartsPie>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  // Offline Jobs Analytics Tab
  const OfflineJobsAnalyticsContent = () => {
    const jobsByCaregiver = useMemo(() => {
      const cgJobs = {};
      filteredOfflineJobs.forEach(j => {
        if (!j.userId) return;
        const caregiver = caregivers?.find(c => c.id === j.userId || c.uid === j.userId);
        const name = caregiver?.username || caregiver?.email || 'Unknown';
        if (!cgJobs[name]) cgJobs[name] = { jobs: 0, revenue: 0 };
        cgJobs[name].jobs++;
        cgJobs[name].revenue += j.amount || 0;
      });
      
      return Object.entries(cgJobs).map(([name, data]) => ({
        name,
        jobs: data.jobs,
        revenue: data.revenue
      })).sort((a, b) => b.revenue - a.revenue);
    }, [filteredOfflineJobs, caregivers]);

    const jobTrend = useMemo(() => {
      const days = eachDayOfInterval({ start: startDate, end: endDate });
      return days.map(day => {
        const dayStart = startOfDay(day);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);
        
        const dayJobs = filteredOfflineJobs.filter(j => {
          const jobDate = parseISO(j.jobDate);
          return jobDate >= dayStart && jobDate <= dayEnd;
        });
        
        return {
          date: format(day, 'MMM dd'),
          jobs: dayJobs.length,
          revenue: dayJobs.reduce((sum, j) => sum + (j.amount || 0), 0)
        };
      });
    }, [filteredOfflineJobs, startDate, endDate]);

    return (
      <div className="space-y-3 md:space-y-6">
        {/* Offline Jobs KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-6">
          <KPICard 
            title="Total Jobs" 
            value={filteredOfflineJobs.length} 
            change={34.2}
            icon={FileText}
            gradient="from-orange-500 to-amber-600"
          />
          <KPICard 
            title="Total Revenue" 
            value={filteredOfflineJobs.reduce((sum, j) => sum + (j.amount || 0), 0)} 
            change={28.7}
            icon={IndianRupee}
            gradient="from-green-500 to-emerald-600"
            prefix="₹"
          />
          <KPICard 
            title="Avg Job Value" 
            value={filteredOfflineJobs.length > 0 
              ? Math.round(filteredOfflineJobs.reduce((sum, j) => sum + (j.amount || 0), 0) / filteredOfflineJobs.length)
              : 0
            } 
            change={12.5}
            icon={Target}
            gradient="from-blue-500 to-cyan-600"
            prefix="₹"
          />
          <KPICard 
            title="Active Trackers" 
            value={new Set(filteredOfflineJobs.map(j => j.userId)).size} 
            change={18.9}
            icon={Users}
            gradient="from-blue-500 to-sky-600"
          />
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-6">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-orange-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Jobs & Revenue Trend" 
              icon={TrendingUp} 
              color="from-orange-500 to-amber-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <ComposedChart data={jobTrend}>
                <defs>
                  <linearGradient id="colorJobRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={COLORS.orange} stopOpacity={0.8}/>
                    <stop offset="95%" stopColor={COLORS.orange} stopOpacity={0.1}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#fed7aa" />
                <XAxis dataKey="date" stroke="#c2410c" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis yAxisId="left" stroke="#c2410c" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis yAxisId="right" orientation="right" stroke="#059669" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#ffedd5', 
                    border: '2px solid #f59e0b',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar yAxisId="left" dataKey="jobs" fill={COLORS.orange} name="Jobs" radius={[8, 8, 0, 0]} />
                <Line 
                  yAxisId="right"
                  type="monotone" 
                  dataKey="revenue" 
                  stroke={COLORS.green} 
                  strokeWidth={chartConfig.strokeWidth}
                  dot={{ fill: COLORS.green, r: 4 }}
                  name="Revenue (₹)"
                />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-gradient-to-br from-blue-50 to-sky-50 rounded-lg md:rounded-xl shadow-sm md:shadow-lg border border-gray-200 md:border-blue-200 md:border-2 p-1 md:p-6 bg-white">
            <SectionHeader 
              title="Top Performers (Jobs Count)" 
              icon={Award} 
              color="from-blue-500 to-sky-600"
            />
            <ResponsiveContainer width="100%" height={220}>
              <BarChart 
                data={jobsByCaregiver.slice(0, 8)} 
                layout="vertical"
                margin={{ top: isMobile ? 5 : 10, right: isMobile ? 5 : 30, left: isMobile ? -15 : 0, bottom: isMobile ? 5 : 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#fce7f3" />
                <XAxis type="number" stroke="#9f1239" tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <YAxis type="category" dataKey="name" stroke="#9f1239" width={isMobile ? 70 : 100} tick={{ fontSize: chartConfig.fontSize }} tickMargin={isMobile ? 5 : 10} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#fdf2f8', 
                    border: '2px solid #ec4899',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                <Bar dataKey="jobs" fill={COLORS.accent} name="Jobs" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Performers Table */}
        <div className="bg-white rounded-xl shadow-lg border-2 border-indigo-200 p-6">
          <SectionHeader 
            title="Offline Jobs Leaderboard" 
            icon={Award} 
            color="from-indigo-500 to-blue-600"
          />
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gradient-to-r from-indigo-100 to-blue-100">
                <tr>
                  <th className="text-left p-4 font-bold text-indigo-900">#</th>
                  <th className="text-left p-4 font-bold text-indigo-900">Caregiver</th>
                  <th className="text-right p-4 font-bold text-indigo-900">Jobs</th>
                  <th className="text-right p-4 font-bold text-indigo-900">Revenue</th>
                </tr>
              </thead>
              <tbody>
                {jobsByCaregiver.map((cg, index) => (
                  <tr key={index} className="border-b border-gray-200 hover:bg-indigo-50 transition-colors">
                    <td className="p-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-white ${
                        index === 0 ? 'bg-gradient-to-br from-yellow-400 to-yellow-600' : 
                        index === 1 ? 'bg-gradient-to-br from-gray-300 to-gray-500' : 
                        index === 2 ? 'bg-gradient-to-br from-orange-400 to-orange-600' : 
                        'bg-gradient-to-br from-indigo-500 to-blue-600'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="p-4 font-semibold text-gray-900">{cg.name}</td>
                    <td className="p-4 text-right text-blue-600 font-bold text-lg">{cg.jobs}</td>
                    <td className="p-4 text-right text-green-600 font-bold text-lg">₹{cg.revenue.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Activity },
    { id: 'revenue', label: 'Revenue & Finance', icon: IndianRupee },
    { id: 'users', label: 'User Analytics', icon: Users },
    { id: 'bookings', label: 'Booking Analytics', icon: ShoppingCart },
    { id: 'caregivers', label: 'Caregiver Performance', icon: Award },
    { id: 'coupons', label: 'Coupon Analytics', icon: Tag },
    { id: 'offline-jobs', label: 'Offline Jobs', icon: FileText },
  ];

  return (
    <AdminSatelliteShell
      title="Reporting & analytics"
      subtitle="Comprehensive business intelligence and insights"
    >
      <div>
        {/* Date Range Filter */}
        <div className="bg-white rounded-lg md:rounded-xl shadow-sm border border-gray-200 p-1 md:p-6 mb-3 md:mb-6">
          <div className="flex flex-wrap items-center gap-2 md:gap-4">
            <label className="text-xs md:text-sm font-bold text-gray-900 flex items-center gap-1 md:gap-2">
              <Calendar className="w-4 h-4 md:w-5 md:h-5 text-[var(--pf-accent)]" />
              <span className="hidden md:inline">Date Range:</span>
            </label>
            <select
              value={dateRange}
              onChange={(e) => {
                setDateRange(e.target.value);
                handleRefreshData();
              }}
              className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base border border-[var(--pf-border)] rounded-md md:rounded-lg focus:ring-1 md:focus:ring-2 focus:ring-[var(--pf-accent)] font-semibold text-[var(--pf-ink)]"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="180">Last 6 months</option>
              <option value="365">Last year</option>
              <option value="custom">Custom Range</option>
            </select>
            
            {dateRange === 'custom' && (
              <>
                <input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => {
                    setCustomStartDate(e.target.value);
                    if (customEndDate) handleRefreshData();
                  }}
                  className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base border border-blue-300 md:border-2 rounded-md md:rounded-lg focus:ring-1 md:focus:ring-2 focus:ring-blue-500"
                />
                <span className="text-gray-600 font-bold text-xs md:text-base">to</span>
                <input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => {
                    setCustomEndDate(e.target.value);
                    if (customStartDate) handleRefreshData();
                  }}
                  className="px-2 md:px-4 py-1 md:py-2 text-xs md:text-base border border-blue-300 md:border-2 rounded-md md:rounded-lg focus:ring-1 md:focus:ring-2 focus:ring-blue-500"
                />
              </>
            )}
            
            <span className="text-[10px] md:text-sm text-gray-600 font-semibold bg-blue-100 px-2 md:px-4 py-1 md:py-2 rounded-md md:rounded-lg">
              {format(startDate, 'MMM dd, yyyy')} - {format(endDate, 'MMM dd, yyyy')}
            </span>
            
            <button
              onClick={handleRefreshData}
              disabled={isRefreshing}
              className={`flex items-center gap-1 md:gap-2 px-2 md:px-4 py-1 md:py-2 text-xs md:text-base rounded-md md:rounded-lg font-semibold transition-all ${
                isRefreshing
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-gradient-to-r from-blue-600 to-sky-600 text-white hover:shadow-lg hover:scale-105'
              }`}
              title="Refresh data"
            >
              <RefreshCw className={`w-3 h-3 md:w-4 md:h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span className="hidden md:inline">{isRefreshing ? 'Refreshing...' : 'Refresh'}</span>
            </button>
            
            <div className="hidden md:flex items-center gap-2 text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg ml-auto">
              <Clock className="w-4 h-4 text-gray-500" />
              <span className="font-medium">Last updated:</span>
              <span className="font-semibold text-blue-600">{getTimeAgo(lastUpdated)}</span>
            </div>
          </div>

          {/* Info Banner */}
          <div className="hidden md:block mt-4 pt-4 border-t border-blue-100">
            <p className="text-xs text-gray-500 flex items-center gap-2">
              <Activity className="w-4 h-4 text-blue-500" />
              <span>
                Data automatically refreshes every <strong className="text-blue-600">3 hours</strong>. 
                Click "Refresh" to manually update the analytics based on your selected date range.
              </span>
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg md:rounded-xl shadow-md md:shadow-lg border border-blue-200 md:border-2 mb-3 md:mb-6">
          <div className="border-b border-blue-100 md:border-b-2 overflow-x-auto">
            <nav className="flex justify-between md:justify-start md:gap-2 p-2 md:p-3">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center justify-center gap-1 md:gap-2 px-2 md:px-6 py-1.5 md:py-3 text-xs md:text-base rounded-md md:rounded-lg font-bold transition-all whitespace-nowrap flex-1 md:flex-none ${
                      activeTab === tab.id
                        ? 'bg-gradient-to-r from-blue-600 to-sky-600 text-white shadow-md md:shadow-lg scale-105'
                        : 'text-gray-600 hover:bg-blue-100 hover:text-blue-700'
                    }`}
                  >
                    <Icon className="w-4 h-4 md:w-6 md:h-6" />
                    <span className="hidden sm:inline">{tab.label}</span>
                  </button>
                );
              })}
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'overview' && <OverviewContent />}
            {activeTab === 'revenue' && <RevenueFinanceContent />}
            {activeTab === 'users' && <UserAnalyticsContent />}
            {activeTab === 'bookings' && <BookingAnalyticsContent />}
            {activeTab === 'caregivers' && <CaregiverPerformanceContent />}
            {activeTab === 'coupons' && <CouponAnalyticsContent />}
            {activeTab === 'offline-jobs' && <OfflineJobsAnalyticsContent />}
          </div>
        </div>
      </div>
    </AdminSatelliteShell>
  );
};

export default Reporting;
